import { ensureCognitoUser, requestMagicLink } from "@src/appV2/Auth/cognito/cognitoAuth";
import { mapCognitoError } from "@src/appV2/Auth/cognito/mapCognitoError";
import {
  recordLoginAttemptInitiated,
  recordTerminalLoginFailure,
} from "@src/appV2/Auth/loginJourneyTelemetry";
import {
  classifyCognitoError,
  IDENTITY_PROVIDER,
  isNetworkFailure,
  logAuthRedirectFailure,
  LOGIN_OUTCOME,
  LOGIN_REASON,
  setLoginIdentityProvider,
} from "@src/appV2/Auth/loginRum";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";

interface SendLoginEmailParams {
  email: string;
  requestedURL?: string | undefined;
  /**
   * Skip the `ensureCognitoUser` JIT-import step. Set this on a resend: the
   * user was already imported on the initial send, so re-running it just fires
   * the PreSignUp Lambda again for no benefit (DEVOP-5526). Leave it off for
   * the first send so legacy users are still imported.
   */
  skipEnsureUser?: boolean;
  /**
   * Cognito username resolved by a previous send, passed on a resend so it
   * reuses that username instead of the (possibly-inactive) email alias.
   */
  username?: string;
}

export type SendLoginEmailResult =
  | {
      success: true;
      authUsername: string;
      hasUsablePassword?: boolean;
      hasVerifiedEmail?: boolean;
    }
  | { success: false; error: string };

/**
 * Sends a Cognito magic link login email.
 *
 * Resolves the concrete Cognito username once: the first send JIT-imports the
 * user via `ensureCognitoUser` (legacy workplace users exist in `facilityusers`
 * but not yet in the Cognito pool) and reuses the username it returns, so the
 * CUSTOM_AUTH flow initiates with a username that resolves even when the email
 * alias is inactive (`email_verified=false`). CUSTOM_AUTH does not invoke the
 * PreAuthentication Lambda, so the import has to be triggered from the client.
 *
 * The resolved username is returned so the caller can pass it back on a resend
 * (`username` + `skipEnsureUser`), avoiding a redundant PreSignUp round trip.
 * `requestMagicLink` itself never probes — it uses the username resolved here.
 */
export async function sendLoginEmail({
  email,
  skipEnsureUser = false,
  username: providedUsername,
}: SendLoginEmailParams): Promise<SendLoginEmailResult> {
  setLoginIdentityProvider(IDENTITY_PROVIDER.COGNITO_MAGIC_LINK);
  // Open the journey at the true first attempt (before provisioning/sending),
  // so it captures the email and starts the clock even if the send fails.
  recordLoginAttemptInitiated({ method: IDENTITY_PROVIDER.COGNITO_MAGIC_LINK, email });

  try {
    let authUsername: string;
    let hasUsablePassword: boolean | undefined;
    let hasVerifiedEmail: boolean | undefined;
    if (providedUsername) {
      authUsername = providedUsername;
    } else if (skipEnsureUser) {
      authUsername = email;
    } else {
      const ensured = await ensureCognitoUser(email);
      authUsername = ensured.cognitoUsername;
      hasUsablePassword = ensured.hasUsablePassword;
      hasVerifiedEmail = ensured.hasVerifiedEmail;
    }

    await requestMagicLink({ email, authUsername });
    logEvent(APP_V2_APP_EVENTS.COGNITO_MAGIC_LINK_REQUESTED, { outcome: LOGIN_OUTCOME.SUCCESS });
    if (typeof hasVerifiedEmail === "boolean") {
      return { success: true, authUsername, hasUsablePassword, hasVerifiedEmail };
    }
    if (typeof hasUsablePassword === "boolean") {
      return { success: true, authUsername, hasUsablePassword };
    }
    return { success: true, authUsername };
  } catch (error: unknown) {
    const mappedError = mapCognitoError(error, "magic-link-request");
    const { outcome, reason } =
      mappedError.code === "auth/unknown" && isNetworkFailure(error)
        ? { outcome: LOGIN_OUTCOME.EXPECTED_ERROR, reason: LOGIN_REASON.NETWORK_FAILURE }
        : classifyCognitoError(mappedError.code);
    recordTerminalLoginFailure();
    logAuthRedirectFailure({
      error,
      metadata: {
        errorCode: mappedError.code,
        errorMessage: mappedError.message,
        email,
        outcome,
        reason,
      },
    });
    return { success: false, error: mappedError.message };
  }
}
